package one.healthium.bland.presentation.feature.today

import androidx.compose.ui.text.TextMeasurer
import androidx.compose.ui.text.TextStyle
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.format
import kotlinx.datetime.format.DayOfWeekNames
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import one.healthium.base.utils.Formatters.formatCalories2
import one.healthium.base.utils.endOfWeek
import one.healthium.base.utils.isSameDay
import one.healthium.base.utils.isToday
import one.healthium.base.utils.startOfWeek
import one.healthium.base.utils.toLocalDate
import one.healthium.base.utils.toLocalDateTime
import one.healthium.domain.user.models.FoodLogModel

internal object ChartMapper {
    fun buildChartList(
        foods: List<FoodLogModel>,
        today: LocalDate,
        textMeasurer: TextMeasurer,
        labelStyle: TextStyle,
        labelStyleActive: TextStyle,
    ): List<ChartPoint> {
        if (foods.isEmpty()) return emptyList()

        val firstFoodLogStart = foods.first().date.toLocalDate().startOfWeek()
        val secondWeekStart = today.startOfWeek().minus(DatePeriod(days = 7))
        val start = if (firstFoodLogStart > secondWeekStart) {
            firstFoodLogStart
        } else {
            secondWeekStart
        }
        val end = today.endOfWeek()

        val maxCalories = foods.maxOfOrNull { it.totalCalories } ?: 1f

        return buildList {
            var weekDay = start
            while (weekDay <= end) {
                val totalCalories = foods.firstOrNull {
                    it.date.toLocalDateTime().isSameDay(weekDay)
                }?.totalCalories ?: 0f

                val active = weekDay.isToday()
                add(
                    ChartPoint(
                        value = totalCalories / maxCalories,
                        label = textMeasurer.measure(
                            text = weekDay.format(DayOfWeekFormat),
                            style = if (active) labelStyleActive else labelStyle
                        ),
                        active = active,
                        displayValue = textMeasurer.measure(
                            text = totalCalories.formatCalories2(roundToK = true),
                            style = labelStyle
                        )
                    )
                )
                weekDay = weekDay.plus(DatePeriod(days = 1))
            }
        }
    }

    private val emptyValues = arrayOf(0.3f, 0.45f, 0.35f, 0.55f, 0.4f, 0.5f, 0.45f)

    fun buildEmptyChartList(
        today: LocalDate,
        textMeasurer: TextMeasurer,
        labelStyle: TextStyle,
    ): List<ChartPoint> {
        val startOfWeek = today.startOfWeek()
        val endOfWeek = today.endOfWeek()
        var index = 0

        return buildList {
            var weekDay = startOfWeek
            while (weekDay <= endOfWeek) {
                add(
                    ChartPoint(
                        value = emptyValues[index],
                        label = textMeasurer.measure(
                            text = weekDay.format(DayOfWeekFormat),
                            style = labelStyle
                        ),
                        active = false,
                        displayValue = textMeasurer.measure(
                            text = "",
                            style = labelStyle
                        )
                    )
                )
                weekDay = weekDay.plus(DatePeriod(days = 1))
                index += 1
            }
        }
    }
}

private val DayOfWeekFormat = LocalDate.Format {
    dayOfWeek(DayOfWeekNames.ENGLISH_ABBREVIATED)
}
